// Island tops (a faceted grid that samples world.terrain() and is clipped exactly at
// world.edgeRadius) + the cobble path + the cliff lip + stacked broken strata underneath.
// floating_island.png: flat plate, broken slabs that taper and darken into the haze.

import * as THREE from 'three';
import { World, Island, NO_GROUND } from '../../../sim/world';
import { valueNoise, smoothstep, hash32 } from '../../../sim/core';
import { PAL } from '../palette';
import { Geo, C, Jit, weldNormals } from './geo';
import { GroundField } from './field';
import { Clock, type Pause } from '../warm';

const cGold = C(PAL.grassGold), cGoldTip = C(PAL.grassGoldTip), cGreen = C(PAL.grassGreen), cGreenDeep = C(PAL.grassGreenDeep);
const cDirt = C(PAL.dirt), cTan = C(PAL.tan), cBrick = C(PAL.brick), cUnder = C(PAL.underside), cDeep = C(PAL.deepShadow);
const cCobble = C(PAL.cobble), cCream = C(PAL.warmCream), cOlive = C(PAL.olive);
const cFloor = new THREE.Color(0xd8a030), cFloorLight = new THREE.Color(0xf0bc4c), cFloorGreen = new THREE.Color(0xa8a23c);
const cFloorSienna = new THREE.Color(0xb47830);
const tmpC = new THREE.Color();
const tmpC2 = new THREE.Color();
const tmpC3 = new THREE.Color(), tmpC4 = new THREE.Color();

export interface IslandGeo { top: Geo; under: Geo; path: THREE.BufferGeometry; }

/** (in slices: a background island build, client/render/warm.ts) */
export function* buildIslandsSteps(world: World, field: GroundField, quality: number, pause: Pause): Generator<unknown, IslandGeo, unknown> {
  const top = new Geo(), under = new Geo();
  const clk = new Clock(pause);
  for (const isl of world.islands) {
    const cs = isl.main ? (quality <= 2 ? 2.4 : 1.75) : 1.5;
    const rim = yield* buildTop(world, field, isl, cs, top, under, clk);
    yield* buildStrata(world, isl, rim, under, quality, clk);
  }
  yield* clk.tick('terrain:weld');
  weldNormals(under, 0.2); // rounded rock, not crystal fans; real edges stay crisp
  yield* clk.tick('terrain:path');
  return { top, under, path: buildPath(world, field) };
}

/** Signed distance inside the island edge (positive inside). */
function edgeS(world: World, isl: Island, x: number, z: number) {
  const dx = x - isl.cx, dz = z - isl.cz, d = Math.hypot(dx, dz);
  if (d < 1e-6) return isl.radius;
  return world.edgeRadius(isl, dx / d, dz / d) - d;
}

function heightIn(world: World, isl: Island, x: number, z: number) {
  let h = world.terrain(x, z);
  if (h !== NO_GROUND) return h;
  // a point on the clip boundary: nudge toward the centre
  const dx = isl.cx - x, dz = isl.cz - z, d = Math.hypot(dx, dz) || 1;
  for (let k = 1; k < 6; k++) {
    h = world.terrain(x + (dx / d) * 0.03 * k, z + (dz / d) * 0.03 * k);
    if (h !== NO_GROUND) return h;
  }
  return isl.baseY - 1.6;
}

function groundColor(world: World, field: GroundField, x: number, z: number, ny: number, y: number, s: number, out: THREE.Color) {
  const seed = world.seed;
  const n = valueNoise(seed ^ 0x51, x / 7, z / 7);
  // the meadow floor reads as the grass's shaded body (gaps between blades = depth)
  out.copy(cFloor).lerp(cFloorLight, n * 0.6);
  const drift = field.driftAt(x, z);
  if (drift > 0) out.lerp(cFloorGreen, Math.min(0.85, drift * 0.9)); else out.lerp(cFloorSienna, Math.min(0.9, -drift * 0.95));
  const dirt = field.dirtAt(x, z);
  out.lerp(cDirt, dirt * 0.75);
  const pd = field.pathDist(x, z);
  out.lerp(cDirt, smoothstep(1.4, -0.2, pd) * 0.7);
  const tm = world.townMask(x, z);
  if (tm > 0) out.lerp(tmpC.copy(cDirt).lerp(cOlive, 0.2), tm * 0.45 * (0.6 + 0.4 * n));
  // slopes: worn earth, then banded warm rock (the mesa flanks)
  if (ny < 0.88) {
    const band = Math.floor(y * 0.9 + valueNoise(seed, x / 5, z / 5) * 1.5) & 3;
    tmpC2.copy(band === 0 ? cBrick : band === 1 ? cTan : band === 2 ? cDirt : cTan).multiplyScalar(band === 2 ? 0.92 : 1);
    out.lerp(cDirt, smoothstep(0.88, 0.78, ny) * 0.7);
    out.lerp(tmpC2, smoothstep(0.8, 0.62, ny));
  }
  // the lip greens and deepens toward the cliff
  if (s < 6) out.lerp(cGreenDeep, smoothstep(6, 0.5, s) * 0.55);
  const j = ((hash32(Math.floor(x * 3.1), Math.floor(z * 3.1), seed) & 255) / 255 - 0.5) * 0.07;
  out.multiplyScalar(1 + j);
  return out;
}

interface Rim { pts: { x: number; z: number; y: number; yb: number }[]; bottom: number; }

function* buildTop(world: World, field: GroundField, isl: Island, cs: number, top: Geo, under: Geo, clk: Clock): Generator<unknown, Rim, unknown> {
  _steep = under; _steepSeen.clear(); // cliff faces (mesa / butte / canyon walls) are drawn as strata rock
  const R = isl.radius * 1.03;
  const n = Math.ceil((2 * R) / cs);
  const ox = isl.cx - (n * cs) / 2, oz = isl.cz - (n * cs) / 2;
  // corner caches
  const S = new Float32Array((n + 1) * (n + 1));
  const H = new Float32Array((n + 1) * (n + 1));
  for (let j = 0; j <= n; j++) {
    yield* clk.tick('terrain:corners');
    for (let i = 0; i <= n; i++) {
      const x = ox + i * cs, z = oz + j * cs, k = j * (n + 1) + i;
      S[k] = edgeS(world, isl, x, z);
      H[k] = S[k] >= 0 ? heightIn(world, isl, x, z) : 0;
    }
  }
  // the island's lowest ground (a canyon floor, a lagoon basin): the underside must stay below
  // it, or the lip cap and the strata roof over the canyon (seen from its floor, a dark ceiling)
  let floor = 1e9;
  for (let k = 0; k < S.length; k++) if (S[k] >= 0) floor = Math.min(floor, H[k]);
  const cut = floor < isl.baseY - 6 ? floor - 1.5 : 1e9;
  const [DX, DZ, HY] = cliffRelax(S, H, n, cs); // de-staircase cliff contours (render only)
  const rimPts: Rim['pts'] = [];
  let lipBottom = 1e9;
  const poly: { x: number; z: number; y: number; s: number; cross: 0 | 1 | 2 }[] = [];
  const jit = new Jit(isl.seed);
  const depthAt = (x: number, z: number) => 2.4 + valueNoise(isl.seed ^ 0x3d, x / 6, z / 6) * 2.6;

  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
    if (i === 0) yield* clk.tick('terrain:top');
    const ks = [j * (n + 1) + i, j * (n + 1) + i + 1, (j + 1) * (n + 1) + i + 1, (j + 1) * (n + 1) + i];
    const cx = [ox + i * cs, ox + (i + 1) * cs, ox + (i + 1) * cs, ox + i * cs];
    const cz = [oz + j * cs, oz + j * cs, oz + (j + 1) * cs, oz + (j + 1) * cs];
    let nin = 0;
    for (const k of ks) if (S[k] >= 0) nin++;
    if (nin === 0) continue;
    if (nin === 4) {
      // alternate the diagonal for a less mechanical facet pattern
      const v = ks.map((k, t) => [cx[t] + DX[k], HY[k], cz[t] + DZ[k]]);
      emitTri(world, field, isl, top, v[0], v[3], v[2], cs);
      emitTri(world, field, isl, top, v[0], v[2], v[1], cs);
      continue;
    }
    // Sutherland-Hodgman against s >= 0 (s is ~linear across a cell)
    poly.length = 0;
    for (let t = 0; t < 4; t++) {
      const a = t, b = (t + 1) % 4;
      const sa = S[ks[a]], sb = S[ks[b]];
      if (sa >= 0) poly.push({ x: cx[a], z: cz[a], y: H[ks[a]], s: sa, cross: 0 });
      if ((sa >= 0) !== (sb >= 0)) {
        const u = sa / (sa - sb);
        const x = cx[a] + (cx[b] - cx[a]) * u, z = cz[a] + (cz[b] - cz[a]) * u;
        poly.push({ x, z, y: heightIn(world, isl, x, z), s: 0, cross: sa >= 0 ? 1 : 2 }); // 1 exit, 2 enter
      }
    }
    if (poly.length < 3) continue;
    for (let t = 1; t < poly.length - 1; t++) {
      const a = poly[0], b = poly[t], c = poly[t + 1];
      // cell corners go +x then +z: that is clockwise seen from +y, so flip for an upward face
      emitTri(world, field, isl, top, [a.x, a.y, a.z], [c.x, c.y, c.z], [b.x, b.y, b.z], cs);
    }
    // boundary segments: exit -> next enter, become the cliff lip walls
    for (let t = 0; t < poly.length; t++) {
      const p = poly[t], q = poly[(t + 1) % poly.length];
      if (p.cross !== 1 || q.cross !== 2) continue;
      const pb = Math.min(p.y - depthAt(p.x, p.z), cut), qb = Math.min(q.y - depthAt(q.x, q.z), cut);
      lipWall(isl, under, p.x, p.y, p.z, pb, q.x, q.y, q.z, qb, jit);
      rimPts.push({ x: p.x, z: p.z, y: p.y, yb: pb });
      lipBottom = Math.min(lipBottom, pb, qb);
      // underside cap of the lip slab (fan to below the centre; mostly hidden by the strata)
      const cy = Math.min(isl.baseY - 4.2, cut - 0.5);
      orientTri(under, [isl.cx, cy, isl.cz], [p.x, pb, p.z], [q.x, qb, q.z], -1, tmpC.copy(cRockEarth).lerp(cWarmRim, 0.15).multiplyScalar(0.8));
    }
  }
  return { pts: rimPts, bottom: lipBottom };
}

/**
 * The height field is sampled on a square grid, so a cliff whose edge runs diagonally to it is a
 * staircase: alternate cells drop the wall a whole cell later, and the wall triangles become a row
 * of shark-tooth fins. Relax the cliff corners sideways (x/z only, y untouched) toward the
 * neighbours at about their own height -- the rim corners along the rim, the foot corners along
 * the foot -- so each contour becomes a smooth line. Three Jacobi passes, offsets capped at 0.55
 * of a cell, nothing moved near the island edge (where the lip walls attach).
 */
function cliffRelax(S: Float32Array, H: Float32Array, n: number, cs: number): [Float32Array, Float32Array, Float32Array] {
  const W = n + 1, N = W * W;
  const DX = new Float32Array(N), DZ = new Float32Array(N);
  const cliff = new Uint8Array(N);
  for (let j = 0; j <= n; j++) for (let i = 0; i <= n; i++) {
    const k = j * W + i;
    if (S[k] < 3) continue;
    let big = 0;
    for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const ii = i + di, jj = j + dj;
      if (ii < 0 || jj < 0 || ii > n || jj > n) continue;
      const m = jj * W + ii;
      if (S[m] >= 0) big = Math.max(big, Math.abs(H[m] - H[k]));
    }
    if (big > Math.max(1.5, cs * 0.9)) cliff[k] = 1;
  }
  const tx = new Float32Array(N), tz = new Float32Array(N);
  for (let pass = 0; pass < 3; pass++) {
    tx.set(DX); tz.set(DZ);
    for (let j = 1; j < n; j++) for (let i = 1; i < n; i++) {
      const k = j * W + i;
      if (!cliff[k]) continue;
      let sx = 0, sz = 0, c = 0;
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        if (!di && !dj) continue;
        const m = (j + dj) * W + i + di;
        if (!cliff[m] || Math.abs(H[m] - H[k]) > 0.9) continue;
        sx += di * cs + DX[m]; sz += dj * cs + DZ[m]; c++;
      }
      if (c < 2) continue;
      let x = 0.5 * DX[k] + 0.5 * (sx / c), z = 0.5 * DZ[k] + 0.5 * (sz / c);
      const l = Math.hypot(x, z), cap = cs * 0.55;
      if (l > cap) { x *= cap / l; z *= cap / l; }
      tx[k] = x; tz[k] = z;
    }
    DX.set(tx); DZ.set(tz);
  }
  // corners part-way down a wall (a much higher AND a much lower neighbour) are sampled at
  // arbitrary heights, which zigzags the wall into teeth: Laplacian-smooth their y (two passes).
  // Nobody stands on a cliff face, and rim and foot corners keep their exact height.
  const HY = Float32Array.from(H);
  const mid = new Uint8Array(N);
  for (let j = 1; j < n; j++) for (let i = 1; i < n; i++) {
    const k = j * W + i;
    if (!cliff[k]) continue;
    let up = 0, dn = 0;
    for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
      const m = (j + dj) * W + i + di;
      if (S[m] < 0) continue;
      if (H[m] - H[k] > 1.5) up = 1;
      if (H[k] - H[m] > 1.5) dn = 1;
    }
    if (up && dn) mid[k] = 1;
  }
  const ty = new Float32Array(N);
  for (let pass = 0; pass < 2; pass++) {
    ty.set(HY);
    for (let j = 1; j < n; j++) for (let i = 1; i < n; i++) {
      const k = j * W + i;
      if (!mid[k]) continue;
      let sy = 0, c = 0;
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        if (!di && !dj) continue;
        const m = (j + dj) * W + i + di;
        if (S[m] < 0) continue;
        sy += HY[m]; c++;
      }
      if (c) ty[k] = 0.5 * HY[k] + 0.5 * (sy / c);
    }
    HY.set(ty);
  }
  return [DX, DZ, HY];
}

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _n = new THREE.Vector3();
/** Emit a triangle whose normal has the sign `upSign` along y (+1 up, -1 down). */
function orientTri(g: Geo, a: number[], b: number[], c: number[], upSign: number, col: THREE.Color) {
  _a.set(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
  _b.set(c[0] - a[0], c[1] - a[1], c[2] - a[2]);
  _n.crossVectors(_a, _b);
  if (_n.y * upSign >= 0) g.tri(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], col);
  else g.tri(a[0], a[1], a[2], c[0], c[1], c[2], b[0], b[1], b[2], col);
}

const _na = [0, 1, 0], _nb = [0, 1, 0], _nc = [0, 1, 0];
const _ca = new THREE.Color(), _cb = new THREE.Color(), _cc = new THREE.Color();
function vtx(world: World, field: GroundField, isl: Island, p: number[], n: number[], c: THREE.Color) {
  field.normalAt(p[0], p[2], n);
  groundColor(world, field, p[0], p[2], n[1], p[1], edgeS(world, isl, p[0], p[2]), c.set(0, 0, 0));
}
/** Smooth ground: per-vertex normals from the height field and per-vertex painted colour. */
function emitTri(world: World, field: GroundField, isl: Island, g: Geo, a: number[], b: number[], c: number[], _cs: number) {
  _a.set(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
  _b.set(c[0] - a[0], c[1] - a[1], c[2] - a[2]);
  _n.crossVectors(_a, _b).normalize();
  if (_n.y < 0) { const t = b; b = c; c = t; _n.negate(); }
  if (_n.y < STEEP_NY && _steep) { steepRock(world, field, _steep, a, b, c, _n); return; }
  vtx(world, field, isl, a, _na, _ca);
  vtx(world, field, isl, b, _nb, _cb);
  vtx(world, field, isl, c, _nc, _cc);
  g.triS(a, b, c, _na, _nb, _nc, _ca, _cb, _cc);
}

// ------------------------------------------------------------------ cliff faces
/** Ground steeper than this (face normal y) is a cliff: strata rock, not painted turf. */
const STEEP_NY = 0.72;
let _steep: Geo | null = null;
const _steepSeen = new Set<string>();
const _sc = [new THREE.Color(), new THREE.Color(), new THREE.Color()];
/**
 * A steep top-surface triangle redrawn as rock in the strata material (triplanar, world-y sediment
 * bands, welded smooth normals), so mesa and canyon walls stop reading as smeared turf. The wall
 * itself is left exactly as the height field gives it (after cliffRelax): an earlier "tent" bump
 * on each face drew dotted seams along the long sliver edges. Rim boulders and a talus apron are
 * added where the wall meets level ground.
 */
function steepRock(world: World, field: GroundField, g: Geo, a: number[], b: number[], c: number[], n: THREE.Vector3) {
  const seed = world.seed ^ 0x7e1;
  const pts = [a, b, c];
  for (let k = 0; k < 3; k++) {
    const p = pts[k];
    const m = valueNoise(seed, p[0] / 4.5, p[2] / 4.5 + p[1] / 6);
    // earthy rock, a touch warmer and lighter where the noise is high
    _sc[k].copy(cRockEarth).lerp(cWarmRim, 0.12 + 0.18 * m).multiplyScalar(0.82 + 0.36 * m);
  }
  const hl = Math.hypot(n.x, n.z);
  g.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
  g.col.push(_sc[0].r, _sc[0].g, _sc[0].b, _sc[1].r, _sc[1].g, _sc[1].b, _sc[2].r, _sc[2].g, _sc[2].b);
  if (hl < 0.35) return;
  // where the wall meets level ground: a rounded rim (boulders half-sunk into the lip break the
  // skyline) and a talus apron (fallen blocks at the foot). Each grid vertex decides once.
  const ox = n.x / hl, oz = n.z / hl;
  let hi = a, lo = a;
  for (const p of [b, c]) { if (p[1] > hi[1]) hi = p; if (p[1] < lo[1]) lo = p; }
  for (const [p, rim] of [[hi, true], [lo, false]] as [number[], boolean][]) {
    const key = (p[0] * 10 | 0) + ',' + (p[2] * 10 | 0) + (rim ? 'r' : 't');
    if (_steepSeen.has(key)) continue;
    _steepSeen.add(key);
    field.normalAt(p[0], p[2], _cn);
    if (_cn[1] < 0.72) continue;
    const h = hash32(Math.round(p[0] * 10), Math.round(p[2] * 10), seed);
    const u = (h & 0xffff) / 0xffff, v = ((h >>> 16) & 0xffff) / 0xffff;
    if (rim) {
      if (u > 0.3) continue;
      const r = 0.6 + 0.9 * v;
      lump(g, p[0] + ox * r * 0.2, p[1] - r * 0.5, p[2] + oz * r * 0.2, r, h, 0.7, 1);
    } else {
      if (u > 0.6) continue;
      const r = u < 0.3 ? 0.45 + 0.85 * v : 0.15 + 0.25 * v; // the odd block among chipped debris
      const x = p[0] + ox * (r * 0.8 + 0.3), z = p[2] + oz * (r * 0.8 + 0.3);
      const gy = world.terrain(x, z);
      if (gy === NO_GROUND) continue;
      lump(g, x, gy - r * 0.3, z, r, h, 0.75, 0);
    }
  }
}

/** One rounded, noise-lumped boulder (a 20- or 80-face icosphere), welded smooth later with the rest. */
const _ico: THREE.Vector3[][] = [];
const _lc = new THREE.Color();
function lump(g: Geo, x: number, y: number, z: number, r: number, seed: number, flat: number, det: 0 | 1) {
  if (!_ico[det]) {
    const ig = new THREE.IcosahedronGeometry(1, det);
    const pa = ig.attributes.position as THREE.BufferAttribute;
    _ico[det] = [];
    for (let i = 0; i < pa.count; i++) _ico[det].push(new THREE.Vector3().fromBufferAttribute(pa, i).normalize());
    ig.dispose();
  }
  const so = seed & 0xfff, sx = 1 + ((seed >>> 12) & 15) / 40, ang = ((seed >>> 16) & 255) / 255 * Math.PI;
  const ca = Math.cos(ang), sa = Math.sin(ang);
  const P: number[][] = [];
  for (const d of _ico[det]) {
    const k = 1 + (valueNoise(so, d.x * 1.4 + d.y * 0.6 + 4, d.z * 1.4 - d.y * 0.8) - 0.5) * 0.45;
    const lx = d.x * sx * k * r, lz = d.z * k * r;
    let ly = d.y * flat * k * r;
    if (ly < -0.35 * r) ly = -0.35 * r; // a flat seat
    P.push([x + lx * ca - lz * sa, y + ly, z + lx * sa + lz * ca]);
  }
  for (let i = 0; i < P.length; i += 3) {
    const cy = (P[i][1] + P[i + 1][1] + P[i + 2][1]) / 3;
    const up = smoothstep(y - r * 0.3, y + r * 0.6, cy);
    _lc.copy(cRockEarth).lerp(cWarmRim, 0.1 + 0.2 * up).multiplyScalar(0.7 + 0.45 * up);
    const hx = (P[i][0] + P[i + 1][0] + P[i + 2][0]) / 3 - x, hz = (P[i][2] + P[i + 1][2] + P[i + 2][2]) / 3 - z;
    triH(g, P[i], P[i + 1], P[i + 2], _lc, _lc, _lc, hx, cy - y, hz);
  }
}

const _cn = [0, 1, 0];
const cLipTop = new THREE.Color();
function lipWall(isl: Island, g: Geo, px: number, py: number, pz: number, pb: number, qx: number, qy: number, qz: number, qb: number, jit: Jit) {
  // outward = away from the island centre
  const mx = (px + qx) / 2 - isl.cx, mz = (pz + qz) / 2 - isl.cz;
  const ex = qx - px, ez = qz - pz;
  // normal of a vertical wall p->q (going down) in xz is (ez, -ex) or its negative
  const flip = ez * mx - ex * mz > 0;
  const band = 0.7; // grassy lip band at the top
  const pm = py - band, qm = qy - band;
  const k = 0.94 + jit.next() * 0.1;
  // olive turf lip (a lime band read as a painted stripe round the island)
  cLipTop.copy(cGreenDeep).lerp(cOlive, 0.45).multiplyScalar(0.78 * k);
  const lower = tmpC.copy(cGreenDeep).lerp(cUnder, 0.55).multiplyScalar(k);
  const wq = (a: number[], b: number[], c: number[], d: number[], col: THREE.Color) => (flip ? g.quad(a, d, c, b, col) : g.quad(a, b, c, d, col));
  // quad order: p-top, p-bottom, q-bottom, q-top (normal = (p_b - p_t) x (q_b - p_t))
  wq([px, py + 0.04, pz], [px, pm, pz], [qx, qm, qz], [qx, qy + 0.04, qz], cLipTop);
  // a deep wall (an island cut by a canyon drops its whole rim below the canyon floor): only the
  // first few metres are earthy turf, the rest is a sandstone cliff like the strata under it
  // (a canyon-deep wall keeps only a thin turf band; the rest is cliff)
  const tb = Math.min(pm - pb, qm - qb) > 8 ? 1.2 : 3.5;
  const pr = Math.max(pb, pm - tb), qr = Math.max(qb, qm - tb);
  wq([px, pm, pz], [px, pr, pz], [qx, qr, qz], [qx, qm, qz], lower);
  if (pr - pb > 0.05 || qr - qb > 0.05) {
    const rock = tmpC.copy(cRockEarth).lerp(cRockDark, 0.2).multiplyScalar(k);
    wq([px, pr, pz], [px, pb, pz], [qx, qb, qz], [qx, qr, qz], rock);
  }
}

// ------------------------------------------------------------------ strata
/**
 * floating_island.png underside: a few broad broken plates right under the rim (the strata
 * band), then a hanging mass of jagged, tapering rock shards -- longest under the middle so the
 * whole underside tapers to a dripping point -- darkening and cooling toward their tips.
 */
function* buildStrata(world: World, isl: Island, rim: Rim, g: Geo, quality: number, clk: Clock): Generator<unknown, void, unknown> {
  const rng = new Jit(isl.seed ^ 0x5ab);
  const depth = isl.radius * (isl.main ? 1.05 : 1.0);
  const N = isl.main ? (quality <= 2 ? 52 : 80) : 36;
  const K = isl.main ? 9 : 5;
  let yTop = Math.min(rim.bottom + 1.2, isl.baseY - 2.6);
  const driftA = rng.range(0, Math.PI * 2);
  let dx = 0, dz = 0, scale = 0.97;
  for (let k = 0; k < K; k++) {
    yield* clk.tick('terrain:strata');
    const t = k / (K - 1);
    const thick = (depth / K) * rng.range(0.55, 1.05);
    const side = new THREE.Color().copy(cRockEarth).lerp(cRockDark, 0.15 + t * 0.55).lerp(cCool, t * 0.3);
    // the top lip catches the low sun: a warm rim; lower lips are dark ledges
    // every tier's top lip is a bright sunlit ledge (the faces below it fall into shadow)
    const lip = new THREE.Color().copy(cWarmRim).lerp(side, 0.15 + t * 0.35).multiplyScalar(1.12 - 0.3 * t);
    const sc = scale;
    // each band sits off-centre (about a tenth of its radius, less for the top one under the
    // rim), so the stack reads as eroded, not as a pile of concentric discs
    const oa = rng.range(0, Math.PI * 2), om = isl.radius * sc * (k === 0 ? 0.04 : rng.range(0.07, 0.12));
    slab(world, isl, g, rng, isl.cx + dx + Math.cos(oa) * om, isl.cz + dz + Math.sin(oa) * om, (ux, uz) => world.edgeRadius(isl, ux, uz) * sc, N, yTop, yTop - thick, side, lip, false);
    // broken pieces of this level: flat horizontal chunks hanging off its edge, a bit lower
    const nb = isl.main ? Math.max(1, 6 - k) : 3 + (k < 2 ? 1 : 0);
    for (let c = 0; c < nb; c++) {
      const a = rng.range(0, Math.PI * 2);
      const er = world.edgeRadius(isl, Math.cos(a), Math.sin(a)) * sc * rng.range(0.82, 0.98);
      const cr = isl.radius * rng.range(0.06, 0.13) * (1 - 0.4 * t);
      const y = yTop - thick * rng.range(0.3, 0.9);
      const th = thick * rng.range(0.5, 0.9);
      const cs = new THREE.Color().copy(side).multiplyScalar(rng.range(0.85, 1.05));
      // a slumped block: a rounded, noise-lumped mass sagging off the band's edge (a small lofted
      // plate read as a cuboid column)
      lump(g, isl.cx + dx + Math.cos(a) * er * 0.92, y - th * 0.4, isl.cz + dz + Math.sin(a) * er * 0.92, Math.max(cr, th * 0.5) * 0.85, (rng.next() * 0xffffff) | 0, 0.55, 1);
    }
    yTop -= thick * rng.range(0.82, 0.95);
    // each level steps inward, the whole mass drifting a little to one side
    scale *= rng.range(0.78, 0.86);
    // spiral: each level's centre walks round, so the stack twists down to a point
    const sa = driftA + k * 0.75;
    dx += Math.cos(sa) * isl.radius * 0.045 * (0.3 + t);
    dz += Math.sin(sa) * isl.radius * 0.045 * (0.3 + t);
  }
  // the tip: one tapering, asymmetric eroded mass (a pointed plate lofted from a lumpy outline,
  // welded smooth) with a couple of thick lobes hanging off it -- not a field of straight cones
  {
    const tr = isl.radius * scale * 0.5, th = depth * 0.16;
    slab(world, isl, g, rng, isl.cx + dx, isl.cz + dz, () => tr, isl.main ? 16 : 11, yTop + 0.6, yTop + 0.6 - th, tmpC3.copy(cRockEarth).lerp(cRockDark, 0.45), tmpC4.copy(cRockEarth).lerp(cWarmRim, 0.2), true);
    for (let i = 0; i < 2; i++) {
      const a = driftA + (i ? 2.4 : 0) + rng.range(-0.5, 0.5), rr = tr * rng.range(0.35, 0.6);
      shard(g, rng, isl.cx + dx + Math.cos(a) * rr, yTop + 0.2, isl.cz + dz + Math.sin(a) * rr, tr * rng.range(0.32, 0.45), depth * rng.range(0.14, 0.24), driftA, 0);
    }
  }
  // a few longer hanging chunks under the middle
  const nShard = isl.main ? 26 : 4;
  for (let i = 0; i < nShard; i++) {
    yield* clk.tick('terrain:shards');
    const a = rng.range(0, Math.PI * 2);
    const u = Math.sqrt(rng.next()) * 0.9;
    const er = world.edgeRadius(isl, Math.cos(a), Math.sin(a)) * scale / 0.75;
    const x = isl.cx + dx + Math.cos(a) * er * u, z = isl.cz + dz + Math.sin(a) * er * u;
    const big = rng.next() < 0.45;
    shard(g, rng, x, yTop + rng.range(0.5, 3), z, isl.radius * (big ? rng.range(0.07, 0.12) : rng.range(0.025, 0.06)), depth * (big ? rng.range(0.3, 0.55) : rng.range(0.12, 0.35)) * (1 - 0.4 * u), driftA, u);
  }
  // jagged shards off the outer levels too, so the silhouette is ragged, not a stepped cone
  for (let i = 0; i < (isl.main ? 12 : 2); i++) {
    const a = rng.range(0, Math.PI * 2);
    const er = world.edgeRadius(isl, Math.cos(a), Math.sin(a)) * rng.range(0.55, 0.85);
    shard(g, rng, isl.cx + Math.cos(a) * er, rim.bottom - rng.range(2, depth * 0.3), isl.cz + Math.sin(a) * er, isl.radius * rng.range(0.035, 0.07), rng.range(8, 22), driftA, 1);
  }
}

const cShardTop = new THREE.Color(), cShardMid = new THREE.Color(), cShardTip = new THREE.Color();
const cCool = new THREE.Color(0x1f2c30);
const cWarmRim = new THREE.Color(0xc98a3e);
const cRockEarth = new THREE.Color(0x4a3a2b), cRockDark = new THREE.Color(0x262624);
/** One faceted, tapering rock shard hanging from (x, y, z): an irregular 5-6 sided root, a
 *  kinked shoulder, then a point; warm-dark stone at the root going cool and dark to the tip. */
function shard(g: Geo, rng: Jit, x: number, y: number, z: number, r: number, len: number, driftA: number, u: number) {
  const sides = 5 + (rng.next() * 2 | 0);
  const a0 = rng.range(0, Math.PI * 2);
  const rs: number[] = [];
  for (let k = 0; k < sides; k++) rs.push(rng.range(0.7, 1.15));
  len = Math.min(len, r * 3.2); // stubby broken lumps, never needles
  const kink = rng.range(0.3, 0.5);
  const dr = len * 0.12, da = driftA + rng.range(-0.8, 0.8);
  const mx = x + Math.cos(da) * dr * kink + rng.range(-0.3, 0.3) * r, mz = z + Math.sin(da) * dr * kink + rng.range(-0.3, 0.3) * r;
  const tx = x + Math.cos(da) * dr + rng.range(-0.4, 0.4) * r, tz = z + Math.sin(da) * dr + rng.range(-0.4, 0.4) * r;
  const my = y - len * kink, ty = y - len;
  const mr = rng.range(0.66, 0.86);
  const k0 = rng.range(0.9, 1.08);
  cShardTop.copy(cRockEarth).lerp(cRockDark, 0.25 + 0.35 * (1 - u)).multiplyScalar(k0);
  cShardMid.copy(cRockDark).lerp(cCool, 0.35).multiplyScalar(k0);
  cShardTip.copy(cRockDark).lerp(cCool, 0.75).multiplyScalar(0.85);
  const ring = (k: number, cx: number, cy: number, cz: number, rr: number, tw: number) => {
    const a = a0 + tw + (k / sides) * Math.PI * 2;
    return [cx + Math.cos(a) * rr * rs[k % sides], cy + rng.range(-0.15, 0.15) * rr, cz + Math.sin(a) * rr * rs[k % sides]];
  };
  const top: number[][] = [], mid: number[][] = [];
  const tw = rng.range(-0.3, 0.3);
  for (let k = 0; k < sides; k++) { top.push(ring(k, x, y, z, r, 0)); mid.push(ring(k, mx, my, mz, r * mr, tw)); }
  const tip = [tx, ty, tz];
  for (let k = 0; k < sides; k++) {
    const k1 = (k + 1) % sides;
    // outward faces: seen from outside, going around counter-clockwise from above means the
    // downward-going quad (top k, mid k, mid k1, top k1) -- orientTri fixes any reversal
    orientOut(g, top[k], mid[k], mid[k1], x, z, cShardTop);
    orientOut(g, top[k], mid[k1], top[k1], x, z, cShardTop);
    orientOut(g, mid[k], tip, mid[k1], x, z, k % 2 ? cShardMid : cShardTip);
  }
}

const _oa = new THREE.Vector3(), _ob = new THREE.Vector3(), _oc = new THREE.Vector3();
/** Emit a triangle wound so its face normal points away from the vertical axis through (ax, az). */
function orientOut(g: Geo, a: number[], b: number[], c: number[], ax: number, az: number, col: THREE.Color) {
  _oa.set(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
  _ob.set(c[0] - a[0], c[1] - a[1], c[2] - a[2]);
  _oc.crossVectors(_oa, _ob);
  const mx = (a[0] + b[0] + c[0]) / 3 - ax, mz = (a[2] + b[2] + c[2]) / 3 - az;
  if (_oc.x * mx + _oc.z * mz - _oc.y * 0.05 >= 0) g.tri(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], col);
  else g.tri(a[0], a[1], a[2], c[0], c[1], c[2], b[0], b[1], b[2], col);
}

/** Emit a per-vertex-coloured triangle wound so its face normal agrees with the hint (hx, hy, hz). */
function triH(g: Geo, a: number[], b: number[], c: number[], ca: THREE.Color, cb: THREE.Color, cc: THREE.Color, hx: number, hy: number, hz: number) {
  _a.set(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
  _b.set(c[0] - a[0], c[1] - a[1], c[2] - a[2]);
  _n.crossVectors(_a, _b);
  if (_n.x * hx + _n.y * hy + _n.z * hz < 0) { const t = b; b = c; c = t; const tc = cb; cb = cc; cc = tc; }
  g.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
  g.col.push(ca.r, ca.g, ca.b, cb.r, cb.g, cb.b, cc.r, cc.g, cc.b);
}

/** Periodic angular noise: three octaves of seeded sines (integer frequencies, so it wraps). */
function angN(ph: number[], a: number, f0: number): number {
  return Math.sin(f0 * a + ph[0]) * 0.55 + Math.sin((f0 * 2 + 1) * a + ph[1]) * 0.3 + Math.sin((f0 * 5 + 2) * a + ph[2]) * 0.15;
}

const _slabCols: THREE.Color[][] = [];
/**
 * One eroded strata plate, lofted from an irregular noisy outline (no chunk steps, no inset
 * panels): a rounded top lip that overhangs unevenly (in places it juts, in places it has
 * weathered back flush), a dark teal undercut seam, a bulging face and a ragged, tapering
 * bottom. Colours run per vertex, so the warm lip fades into the face instead of sitting on it as
 * a flat lit stripe. Signature unchanged; ~12 tris per outline segment, as before.
 */
function slab(
  world: World, isl: Island, g: Geo, rng: Jit, cx: number, cz: number,
  radius: (ux: number, uz: number) => number, N: number, y0: number, y1: number,
  side: THREE.Color, lip: THREE.Color, pointy: boolean,
) {
  void world; void isl;
  const H = Math.max(0.3, y0 - y1);
  const ph = () => [rng.range(0, 6.283), rng.range(0, 6.283), rng.range(0, 6.283)];
  const pOut = ph(), pLip = ph(), pOv = ph(), pBul = ph(), pBot = ph(), pBrk = ph();
  // small chunks get a much lumpier outline (they are broken pieces, not trimmed plates)
  const small = N < 16;
  const aOut = small ? 0.2 : rng.range(0.15, 0.22), fOut = small ? 2 : 2 + (rng.next() * 2 | 0);
  const taper = pointy ? 0.6 : rng.range(0.86, 0.94);
  const lipH = Math.min(1.3, H * 0.28);
  const seamH = Math.min(1.1, H * 0.16); // tall enough that the undercut is a slope, not a dark shelf
  const ctrB = y1 - (pointy ? H * 0.9 : 0.4 + H * 0.15); // a blunt, broken tip (never a needle)
  const k0 = rng.range(0.92, 1.06);
  // rows: 0 top edge, 1 lip nose, 2 undercut seam, 3 face bulge, 4 lower face, 5 bottom edge
  const notches: number[][] = [];
  for (let i = 0, nn = small ? 1 : 2 + (rng.next() * 2 | 0); i < nn; i++) notches.push([rng.range(0, Math.PI * 2), rng.range(0.08, 0.16), rng.range(0.12, 0.3)]);
  const P: number[][][] = [[], [], [], [], [], []];
  while (_slabCols.length < N) _slabCols.push([0, 1, 2, 3, 4, 5].map(() => new THREE.Color()));
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + (rng.next() - 0.5) * (Math.PI / N) * 0.6;
    const ux = Math.cos(a), uz = Math.sin(a);
    let r = radius(ux, uz) * (1 + aOut * angN(pOut, a, fOut) + (rng.next() - 0.5) * 0.02);
    // bites out of the rim where blocks have slumped away
    for (const nt of notches) { const d = Math.atan2(Math.sin(a - nt[0]), Math.cos(a - nt[0])); r *= 1 - nt[1] * Math.exp(-((d / nt[2]) ** 2)); }
    const ln = angN(pLip, a, 2);                         // lip thickness wander
    const ov = Math.max(0, angN(pOv, a, small ? 1 : 2)); // overhang: juts in places, flush elsewhere
    const bu = angN(pBul, a, 3);
    const bn = angN(pBot, a, small ? 1 : 3) * 0.5 + 0.5;
    // the ledge breaks: where this noise is low the lip has collapsed (no overhang, dropped,
    // pulled in), so every ledge tapers out and resumes instead of running round as a ruled shelf
    const brk = smoothstep(-0.05, -0.45, angN(pBrk, a, 3));
    const yl = y0 - lipH * (0.75 + 0.45 * ln) - lipH * 0.8 * brk;
    const ys = yl - seamH * (0.6 + 0.8 * ov);
    const yb = y1 - H * 0.35 * bn;
    const ym = ys + (yb - ys) * 0.42, yw = ys + (yb - ys) * 0.78;
    const ovb = ov * (1 - brk);
    const rr = [r * (0.975 - 0.03 * brk), r * (1.0 + 0.035 * ovb - 0.025 * brk), r * (0.978 - 0.012 * ovb), r * (0.99 + 0.02 * bu), r * (0.965 - 0.03 * bn), r * taper * (0.94 + 0.08 * bn)];
    const yy = [y0 + 0.08 * ln - lipH * 0.5 * brk, yl, ys, ym + H * 0.06 * bu, yw, yb];
    for (let k = 0; k < 6; k++) P[k].push([cx + ux * rr[k], yy[k], cz + uz * rr[k]]);
    // per-vertex colour: warm sunlit lip (brightest where it juts), teal-dark undercut, earthy
    // face mottled by the bulge, darkening down into the occluded bottom
    const c = _slabCols[i], m = k0 * (0.93 + 0.08 * bu);
    c[0].copy(lip).multiplyScalar(m * (0.95 + 0.12 * ov));
    c[1].copy(lip).lerp(side, 0.3 - 0.2 * ov).multiplyScalar(m);
    c[2].copy(side).lerp(cCool, 0.7).multiplyScalar(0.5 + 0.2 * (1 - ov));
    c[3].copy(side).multiplyScalar(m * (1.0 + 0.1 * bu));
    c[4].copy(side).multiplyScalar(m * 0.75);
    c[5].copy(side).lerp(cWarmRim, 0.12).multiplyScalar(0.6);
  }
  const top = [cx, y0 + 0.05, cz], bot = [cx, ctrB, cz];
  const cTop = tmpC.copy(lip).lerp(side, 0.45).multiplyScalar(0.72 * k0), cBot = tmpC2.copy(side).lerp(cWarmRim, 0.12).multiplyScalar(0.45);
  for (let i = 0; i < N; i++) {
    const j = (i + 1) % N, ci = _slabCols[i], cj = _slabCols[j];
    for (let k = 0; k < 5; k++) {
      const A = P[k][i], B = P[k][j], Cq = P[k + 1][j], D = P[k + 1][i];
      const hx = (A[0] + B[0]) / 2 - cx, hz = (A[2] + B[2]) / 2 - cz;
      // split along the shorter diagonal so the facets follow the rock, not a fixed grain
      const d1 = (A[0] - Cq[0]) ** 2 + (A[1] - Cq[1]) ** 2 + (A[2] - Cq[2]) ** 2;
      const d2 = (B[0] - D[0]) ** 2 + (B[1] - D[1]) ** 2 + (B[2] - D[2]) ** 2;
      if (d1 <= d2) {
        triH(g, A, B, Cq, ci[k], cj[k], cj[k + 1], hx, 0, hz);
        triH(g, A, Cq, D, ci[k], cj[k + 1], ci[k + 1], hx, 0, hz);
      } else {
        triH(g, A, B, D, ci[k], cj[k], ci[k + 1], hx, 0, hz);
        triH(g, B, Cq, D, cj[k], cj[k + 1], ci[k + 1], hx, 0, hz);
      }
    }
    triH(g, top, P[0][i], P[0][j], cTop, ci[0], cj[0], 0, 1, 0);
    triH(g, bot, P[5][i], P[5][j], cBot, ci[5], cj[5], 0, -1, 0);
  }
}

// ------------------------------------------------------------------ path
/**
 * The cobble path as its own textured strip: u across (metres / 2), v along (metres / 2), so
 * the cobble texture keeps a constant stone size through bends. Vertex colour carries the
 * wear: a dusty, grass-grown margin and sun-bleached centre. The ribbon follows the walkable
 * terrain exactly (lifted 4-8 cm), with ragged edges that sink into the ground.
 */
function buildPath(world: World, field: GroundField): THREE.BufferGeometry {
  const P = world.path;
  const pts: { x: number; z: number }[] = [];
  const step = 0.55;
  for (let s = 0; s < P.length - 1; s++) {
    const a = P[s], b = P[s + 1];
    const L = Math.hypot(b.x - a.x, b.z - a.z);
    const m = Math.max(1, Math.round(L / step));
    for (let j = 0; j < m; j++) pts.push({ x: a.x + ((b.x - a.x) * j) / m, z: a.z + ((b.z - a.z) * j) / m });
  }
  pts.push(P[P.length - 1]);
  const tan = pts.map((_, i) => {
    const a = pts[Math.max(0, i - 3)], b = pts[Math.min(pts.length - 1, i + 3)];
    const dx = b.x - a.x, dz = b.z - a.z, l = Math.hypot(dx, dz) || 1;
    return { x: dx / l, z: dz / l };
  });
  const COLS = 10; // across the ribbon (incl. the two ragged margins)
  const pos: number[] = [], nrm: number[] = [], uv: number[] = [], col: number[] = [], idx: number[] = [];
  const n3 = [0, 1, 0];
  const c = new THREE.Color();
  let vAlong = 0;
  let rows = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], t = tan[i];
    if (i > 0) vAlong += Math.hypot(p.x - pts[i - 1].x, p.z - pts[i - 1].z);
    const nx = -t.z, nz = t.x;
    const hw = field.pathHalfWidth(p.x, p.z);
    const h = hash32(i, 77, world.seed ^ 0xc0b);
    for (let k = 0; k <= COLS; k++) {
      const f = k / COLS * 2 - 1; // -1..1
      const edge = k === 0 || k === COLS;
      const rag = edge ? 0.25 + ((h >>> (k === 0 ? 0 : 8)) & 255) / 255 * 0.5 : 0;
      const o = f * hw + Math.sign(f) * rag;
      const x = p.x + nx * o, z = p.z + nz * o;
      let y = world.terrain(x, z);
      if (y === NO_GROUND) y = world.terrain(p.x, p.z);
      if (y === NO_GROUND) y = 0;
      y += edge ? -0.04 : 0.05 + 0.03 * (1 - f * f);
      field.normalAt(x, z, n3);
      pos.push(x, y, z);
      nrm.push(n3[0] * 0.5, 0.5 + n3[1] * 0.5, n3[2] * 0.5);
      uv.push(o * 0.7, vAlong * 0.7);
      // wear: dusty dirt margins, bleached crown, patches of grass growing through
      const wear = valueNoise(world.seed ^ 0x9a7, x / 3, z / 3);
      c.copy(cCream).lerp(cCobble, 0.5 + 0.5 * Math.abs(f));
      c.lerp(cDirt, smoothstep(0.55, 1.0, Math.abs(f)) * 0.6 + (wear > 0.7 ? 0.25 : 0));
      c.lerp(cOlive, smoothstep(0.75, 0.9, wear) * 0.35);
      if (edge) c.lerp(cDirt, 0.5).multiplyScalar(0.85);
      col.push(c.r, c.g, c.b);
    }
    // the town draws its own cobbled main street (town_build.ts street(): x0-5 .. x1+3), so the
    // trail ribbon stops at its ends instead of z-fighting underneath it
    const tw = world.town;
    const inStreet = (q: { x: number; z: number }) => q.x > tw.x0 - 5 && q.x < tw.x1 + 3 && Math.abs(q.z - tw.z) < tw.halfWidth;
    if (i > 0 && !inStreet(p) && !inStreet(pts[i - 1]) && world.terrain(p.x, p.z) !== NO_GROUND && world.terrain(pts[i - 1].x, pts[i - 1].z) !== NO_GROUND) {
      const r0 = (rows - 1) * (COLS + 1), r1 = rows * (COLS + 1);
      for (let k = 0; k < COLS; k++) {
        // across is +n (left of travel), along is +t; (n x t) points up
        idx.push(r0 + k, r0 + k + 1, r1 + k, r0 + k + 1, r1 + k + 1, r1 + k);
      }
    }
    rows++;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}
